<template>
  <div class="ai-log">
    <APageHead
      title="로그 · AI 호출"
      sub="AI 호출 1건이 한 행입니다. 어느 서버·모델이 얼마나 쓰였는지, 예비 서버로 전환됐는지 확인합니다."
    >
      <template #actions>
        <ABtn kind="ghost" icon="settings" @click="goSettings">AI 설정</ABtn>
        <ABtn kind="ghost" icon="refresh" :loading="loading" @click="reload">새로고침</ABtn>
      </template>
    </APageHead>

    <!--
      예비 서버가 응답한 호출이 있으면 그 기간에 1순위 서버 장애가 있었다는 뜻입니다(docs/AI설정_관리자화면_가이드.md §3-2).
      요약 카드만으로는 지나치기 쉬워 배너로 한 번 더 알립니다.
    -->
    <div v-if="stats && stats.fallback_count > 0" class="ai-log__banner">
      <i class="material-icons-round">swap_horiz</i>
      <div>
        <b>최근 {{ stats.days }}일 동안 예비 서버가 {{ stats.fallback_count.toLocaleString() }}건을 처리했습니다.</b>
        <span>그 시간대에 1순위 서버 장애가 있었다는 뜻입니다. 아래 목록에서 "예비" 배지가 붙은 행을 확인하세요.</span>
      </div>
    </div>

    <div v-if="statsError" class="ai-log__banner ai-log__banner--err">
      <i class="material-icons-round">cloud_off</i>
      <div>
        <b>집계를 불러오지 못했습니다.</b>
        <span>{{ statsError }} — 아래 요약 카드와 집계표는 비어 있습니다. 새로고침해 주세요.</span>
      </div>
    </div>

    <!-- 요약 카드 5개. 연결 테스트(profile_test)는 여기 포함되지 않습니다(§3-2). -->
    <div class="ai-log__stats">
      <ACard><AStat label="전체 호출" :value="fmtNum(stats && stats.total_calls)" unit="건" /></ACard>
      <ACard><AStat label="성공" :value="fmtNum(stats && stats.success_count)" unit="건" /></ACard>
      <ACard><AStat label="실패" :value="fmtNum(stats && stats.failed_count)" unit="건" /></ACard>
      <div :class="{ 'ai-log__card--alert': stats && stats.fallback_count > 0 }">
        <ACard>
          <AStat label="예비 서버 처리" :value="fmtNum(stats && stats.fallback_count)" unit="건" />
        </ACard>
      </div>
      <ACard><AStat label="총 토큰" :value="fmtNum(stats && stats.total_tokens)" /></ACard>
    </div>
    <div class="ai-log__stats-note">
      <i class="material-icons-round">info</i>
      요약 카드 5개에는 연결 테스트 호출이 포함되지 않습니다. 아래 집계표·목록에는 나옵니다.
    </div>

    <!-- 필터 -->
    <ACard>
      <div class="ai-log__filters">
        <div class="p-field ai-log__f ai-log__f--sm">
          <label class="p-field__label">기간</label>
          <select v-model.number="filters.days" class="p-input" @change="reload">
            <option :value="1">최근 1일</option>
            <option :value="7">최근 7일</option>
            <option :value="14">최근 14일</option>
            <option :value="30">최근 30일</option>
            <option :value="90">최근 90일</option>
          </select>
        </div>
        <div class="p-field ai-log__f ai-log__f--md">
          <label class="p-field__label">기능</label>
          <select v-model="filters.feature" class="p-input" @change="search">
            <option value="">전체</option>
            <option v-for="f in LOG_FEATURES" :key="f.code" :value="f.code">{{ f.label }}</option>
          </select>
        </div>
        <div class="p-field ai-log__f ai-log__f--md">
          <label class="p-field__label">프로필</label>
          <select v-model="filters.profileCode" class="p-input" @change="search">
            <option value="">전체</option>
            <option v-for="c in knownProfiles" :key="c" :value="c">{{ c }}</option>
          </select>
        </div>
        <div class="p-field ai-log__f ai-log__f--sm">
          <label class="p-field__label">역할</label>
          <select v-model="filters.role" class="p-input" @change="search">
            <option value="">전체</option>
            <option value="primary">1순위</option>
            <option value="fallback">예비</option>
            <option value="test">연결 테스트</option>
          </select>
        </div>
        <div class="p-field ai-log__f ai-log__f--sm">
          <label class="p-field__label">결과</label>
          <select v-model="filters.success" class="p-input" @change="search">
            <option value="">전체</option>
            <option value="true">성공만</option>
            <option value="false">실패만</option>
          </select>
        </div>
        <div class="p-field ai-log__f ai-log__f--sm">
          <label class="p-field__label">개수</label>
          <select v-model.number="pageSize" class="p-input" @change="search">
            <option :value="20">20개</option>
            <option :value="50">50개</option>
            <option :value="100">100개</option>
          </select>
        </div>
        <div class="ai-log__f-btn">
          <ABtn kind="ghost" icon="restart_alt" @click="clearFilters">필터 초기화</ABtn>
        </div>
      </div>
      <div class="ai-log__retain">
        <i class="material-icons-round">schedule</i>
        기록은 90일 뒤 자동으로 지워집니다. 별도의 삭제 기능은 없습니다.
      </div>
    </ACard>

    <!-- 집계표 -->
    <ACard body-flush>
      <template #title>
        <span class="ai-log__sec-title">
          <i class="material-icons-round ai-log__sec-icon">table_chart</i>
          기능 · 프로필 · 모델별 집계
        </span>
      </template>
      <template #sub>모델을 바꿔 가며 속도와 토큰 사용량을 비교합니다. 평균 응답 시간은 성공한 호출만의 평균입니다.</template>

      <AEmpty
        v-if="!statsRows.length"
        title="집계할 호출이 없습니다"
        sub="이 기간에 AI 호출 기록이 없습니다."
      />
      <ATable v-else :columns="statsCols" :rows="statsRows" row-key="_key">
        <template #cell:feature="{ row }">{{ featureLabel(row.feature) }}</template>
        <template #cell:profile_code="{ row }">
          <span class="ai-log__mono">{{ row.profile_code }}</span>
        </template>
        <template #cell:model="{ row }">
          <span v-if="row.model" class="ai-log__mono ai-log__model" :title="row.model">{{ row.model }}</span>
          <span v-else class="ai-log__dash">—</span>
        </template>
        <template #cell:role="{ row }">
          <APill :kind="roleKind(row.role)">{{ roleLabel(row.role) }}</APill>
        </template>
        <template #cell:calls="{ row }">
          <span class="ai-log__mono">{{ fmtNum(row.calls) }}</span>
        </template>
        <template #cell:success_count="{ row }">
          <span class="ai-log__mono">{{ fmtNum(row.success_count) }}</span>
        </template>
        <template #cell:failed_count="{ row }">
          <span :class="['ai-log__mono', { 'ai-log__bad': row.failed_count > 0 }]">{{ fmtNum(row.failed_count) }}</span>
        </template>
        <template #cell:avg_latency_ms="{ row }">
          <span class="ai-log__mono">{{ row.avg_latency_ms === null || row.avg_latency_ms === undefined ? '—' : `${fmtNum(row.avg_latency_ms)}ms` }}</span>
        </template>
        <template #cell:total_tokens="{ row }">
          <span class="ai-log__mono">{{ fmtTokens(row.total_tokens) }}</span>
        </template>
      </ATable>
    </ACard>

    <!-- 목록 -->
    <ACard body-flush>
      <template #title>
        <span class="ai-log__sec-title">
          <i class="material-icons-round ai-log__sec-icon">receipt_long</i>
          호출 기록
        </span>
      </template>
      <template #sub>
        1순위가 실패해 예비로 넘어간 요청은 두 줄로 남습니다.
        질문·답변 내용은 저장되지 않고 글자 수·토큰 수만 기록됩니다.
      </template>
      <template #action><APill kind="info">{{ fmtNum(total) }}건</APill></template>

      <!-- 로딩 중 items 가 비어 있을 때 표를 그리면 표 안쪽 문구가 "데이터가 없습니다" 로 보입니다. -->
      <AEmpty
        v-if="!loading && !items.length"
        title="호출 기록이 없습니다"
        sub="조건을 바꾸거나 기간을 넓혀 보세요."
      />

      <ATable v-else-if="items.length" :columns="cols" :rows="items" row-key="idx">
        <template #cell:created_at="{ row }">
          <span class="ai-log__mono ai-log__sm">{{ fmtClock(row.created_at) }}</span>
        </template>

        <template #cell:feature="{ row }">
          <span class="ai-log__sm">{{ featureLabel(row.feature) }}</span>
        </template>

        <template #cell:profile_code="{ row }">
          <div class="ai-log__profile">
            <span class="ai-log__mono">{{ row.profile_code }}</span>
            <APill v-if="row.profile_code === BOOTSTRAP_PROFILE" kind="err">비상 기본값</APill>
            <span v-else class="ai-log__provider">{{ providerLabel(row.provider_type) }}</span>
          </div>
        </template>

        <template #cell:model="{ row }">
          <span v-if="row.model" class="ai-log__mono ai-log__model" :title="row.model">{{ row.model }}</span>
          <span v-else class="ai-log__dash">—</span>
        </template>

        <template #cell:role="{ row }">
          <div class="ai-log__badges">
            <APill :kind="roleKind(row.role)">{{ roleLabel(row.role) }}</APill>
            <!--
              1순위를 호출하지 않고 끝났다는 표시입니다. 예비가 응답한 행에도,
              예비가 없어 호출 없이 끝난 행에도 붙습니다. 연속으로 보이면 1순위 서버가 죽은 것입니다(§3-1).
            -->
            <APill v-if="row.circuit_skipped_primary" kind="warn">1순위 건너뜀</APill>
          </div>
        </template>

        <template #cell:success="{ row }">
          <div class="ai-log__result">
            <APill :kind="row.success ? 'ok' : 'err'" dot>{{ row.success ? '성공' : '실패' }}</APill>
            <span v-if="!row.success && row.error_type" class="ai-log__err" :title="errorTypeLabel(row.error_type)">
              {{ errorTypeLabel(row.error_type) }}
            </span>
          </div>
        </template>

        <template #cell:latency_ms="{ row }">
          <span class="ai-log__mono">{{ fmtLatency(row.latency_ms) }}</span>
        </template>

        <template #cell:tokens="{ row }">
          <div class="ai-log__tokens">
            <span class="ai-log__mono">{{ fmtTokens(row.total_tokens) }}</span>
            <span class="ai-log__tokens-sub">
              질문 {{ fmtTokens(row.prompt_tokens) }} · 답변 {{ fmtTokens(row.completion_tokens) }}
            </span>
          </div>
        </template>

        <template #cell:chars="{ row }">
          <span class="ai-log__mono ai-log__sm">
            {{ fmtNum(row.prompt_chars) }} / {{ fmtNum(row.response_chars) }}
          </span>
        </template>
      </ATable>

      <div v-if="total > 0" class="ai-log__pager">
        <APagination
          :current-page="currentPage"
          :total="total"
          :page-size="pageSize"
          @change="changePage"
        />
      </div>
    </ACard>

    <!-- 최근 실패 -->
    <ACard v-if="recentFailures.length" body-flush>
      <template #title>
        <span class="ai-log__sec-title">
          <i class="material-icons-round ai-log__sec-icon">error_outline</i>
          최근 실패 10건
        </span>
      </template>
      <template #sub>필터와 무관하게 이 기간의 최근 실패를 보여줍니다.</template>

      <ATable :columns="failCols" :rows="recentFailures" row-key="idx">
        <template #cell:created_at="{ row }">
          <span class="ai-log__mono ai-log__sm">{{ fmtClock(row.created_at) }}</span>
        </template>
        <template #cell:feature="{ row }">
          <span class="ai-log__sm">{{ featureLabel(row.feature) }}</span>
        </template>
        <template #cell:profile_code="{ row }">
          <span class="ai-log__mono">{{ row.profile_code }}</span>
        </template>
        <template #cell:role="{ row }">
          <APill :kind="roleKind(row.role)">{{ roleLabel(row.role) }}</APill>
        </template>
        <template #cell:error_type="{ row }">
          <span class="ai-log__err">{{ errorTypeLabel(row.error_type) || '—' }}</span>
        </template>
        <template #cell:http_status="{ row }">
          <span class="ai-log__mono">{{ row.http_status || '—' }}</span>
        </template>
      </ATable>
    </ACard>
  </div>
</template>

<script>
import APageHead from "@/components/Pineapple/APageHead.vue";
import ACard from "@/components/Pineapple/ACard.vue";
import ABtn from "@/components/Pineapple/ABtn.vue";
import APill from "@/components/Pineapple/APill.vue";
import AStat from "@/components/Pineapple/AStat.vue";
import ATable from "@/components/Pineapple/ATable.vue";
import AEmpty from "@/components/Pineapple/AEmpty.vue";
import APagination from "@/components/Pineapple/APagination.vue";
import { apiErrorMessage } from "@/utils/apiError";
import { formatServerClock } from "@/utils/datetime";
import {
  LOG_FEATURES,
  BOOTSTRAP_PROFILE,
  featureLabel,
  providerLabel,
  errorTypeLabel,
  roleLabel,
  fmtTokens,
  fmtLatency,
} from "@/utils/aiSettings";

export default {
  name: "SystemLogsAiCalls",
  components: { APageHead, ACard, ABtn, APill, AStat, ATable, AEmpty, APagination },
  data() {
    return {
      LOG_FEATURES,
      BOOTSTRAP_PROFILE,
      loading: false,
      items: [],
      stats: null,
      statsError: "",
      total: 0,
      currentPage: 1,
      pageSize: 20,
      filters: {
        days: 7,
        feature: "",
        profileCode: "",
        role: "",
        success: "",
      },
      cols: [
        { key: "created_at",   label: "시각", width: 160 },
        { key: "feature",      label: "기능", width: 120 },
        { key: "profile_code", label: "프로필" },
        { key: "model",        label: "모델" },
        { key: "role",         label: "역할", width: 150 },
        { key: "success",      label: "결과", width: 200 },
        { key: "latency_ms",   label: "소요", width: 90, align: "right" },
        { key: "tokens",       label: "토큰", width: 130, align: "right" },
        { key: "chars",        label: "글자 수 (질문/답변)", width: 130, align: "right" },
      ],
      statsCols: [
        { key: "feature",         label: "기능", width: 130 },
        { key: "profile_code",    label: "프로필" },
        { key: "model",           label: "모델" },
        { key: "role",            label: "역할", width: 100, align: "center" },
        { key: "calls",           label: "호출", width: 80, align: "right" },
        { key: "success_count",   label: "성공", width: 80, align: "right" },
        { key: "failed_count",    label: "실패", width: 80, align: "right" },
        { key: "avg_latency_ms",  label: "평균 응답", width: 100, align: "right" },
        { key: "total_tokens",    label: "토큰", width: 100, align: "right" },
      ],
      failCols: [
        { key: "created_at",   label: "시각", width: 160 },
        { key: "feature",      label: "기능", width: 120 },
        { key: "profile_code", label: "프로필" },
        { key: "role",         label: "역할", width: 100, align: "center" },
        { key: "error_type",   label: "원인" },
        { key: "http_status",  label: "HTTP", width: 80, align: "center" },
      ],
    };
  },
  computed: {
    statsRows() {
      return (this.stats?.rows || []).map((r, i) => ({
        ...r,
        _key: `${r.feature}|${r.profile_code}|${r.model || ''}|${r.role}|${i}`,
      }));
    },
    recentFailures() {
      return this.stats?.recent_failures || [];
    },
    /**
     * 프로필 드롭다운은 이 기간에 실제로 호출된 코드로 만듭니다.
     * 프로필 목록 API 에는 없는 `bootstrap_vllm` 도 로그에는 나오기 때문입니다(§3-1).
     */
    knownProfiles() {
      const set = new Set();
      (this.stats?.rows || []).forEach((r) => r.profile_code && set.add(r.profile_code));
      this.items.forEach((r) => r.profile_code && set.add(r.profile_code));
      if (this.filters.profileCode) set.add(this.filters.profileCode);
      return [...set].sort();
    },
  },
  mounted() {
    this.reload();
  },
  methods: {
    featureLabel,
    providerLabel,
    errorTypeLabel,
    roleLabel,
    fmtTokens,
    fmtLatency,

    /** 값이 없는 것과 0 은 다릅니다. 집계 조회가 실패한 카드가 "0건" 으로 보이면 정상과 구분되지 않습니다. */
    fmtNum(n) {
      return n === null || n === undefined ? "—" : Number(n).toLocaleString();
    },

    fmtClock(v) {
      return formatServerClock(v) || "—";
    },

    roleKind(role) {
      if (role === "fallback") return "warn";
      if (role === "test") return "default";
      return "info";
    },

    async reload() {
      this.currentPage = 1;
      await Promise.all([this.fetchStats(), this.fetchLogs()]);
    },

    search() {
      this.currentPage = 1;
      this.fetchLogs();
    },

    clearFilters() {
      this.filters.feature = "";
      this.filters.profileCode = "";
      this.filters.role = "";
      this.filters.success = "";
      this.search();
    },

    changePage(page) {
      this.currentPage = page;
      this.fetchLogs();
    },

    goSettings() {
      this.$router.push({ name: "SystemAiSettings" });
    },

    async fetchStats() {
      try {
        const res = await this.$axios({
          method: "get",
          url: "/system/logs/ai-calls/stats",
          params: { days: this.filters.days },
        });
        // 봉투의 success 가 false 면 data 가 비어 옵니다. 그대로 그리면 "호출 0건" 처럼 보입니다.
        this.stats = res.data?.success === false ? null : res.data?.data || null;
        this.statsError = this.stats ? "" : "집계를 불러오지 못했습니다.";
      } catch (e) {
        console.error("AI 호출 집계 조회 실패:", e);
        this.stats = null;
        this.statsError = apiErrorMessage(e, "집계를 불러오지 못했습니다.");
      }
    },

    async fetchLogs() {
      this.loading = true;
      try {
        const params = {
          days: this.filters.days,
          page: this.currentPage,
          pageSize: this.pageSize,
        };
        if (this.filters.feature) params.feature = this.filters.feature;
        if (this.filters.profileCode) params.profileCode = this.filters.profileCode;
        if (this.filters.role) params.role = this.filters.role;
        // 빈 문자열은 "전체" 라 보내지 않습니다. 'false' 도 유효한 값이라 truthy 검사를 쓰면 안 됩니다.
        if (this.filters.success !== "") params.success = this.filters.success === "true";

        const res = await this.$axios({
          method: "get",
          url: "/system/logs/ai-calls",
          params,
          loadingBar: true,
        });
        if (res.data?.success === false) {
          this.items = [];
          this.total = 0;
          this.$notifyVue({
            type: "error",
            title: "AI 호출 기록을 불러오지 못했습니다.",
            text: "서버가 조회 실패를 응답했습니다.",
          });
          return;
        }
        const data = res.data?.data || {};
        this.items = data.items || [];
        this.total = data.pagination?.totalCount || 0;
      } catch (e) {
        this.$notifyVue({
          type: "error",
          title: "AI 호출 기록을 불러오지 못했습니다.",
          text: apiErrorMessage(e, ""),
        });
        this.items = [];
        this.total = 0;
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/pineapple";

.ai-log {
  display: flex;
  flex-direction: column;
  gap: 18px;

  &__banner {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 14px 18px;
    border-radius: $p-radius-md;
    background: $p-warn-soft;
    border-left: 3px solid $p-warn;
    color: $p-warn-ink;

    > i { font-size: 20px; flex-shrink: 0; }

    > div {
      display: flex;
      flex-direction: column;
      gap: 3px;
    }

    b { font-size: 13px; }
    span { font-size: 12px; line-height: 1.6; }

    &--err {
      background: $p-err-soft;
      border-left-color: $p-err;
      color: $p-err-ink;
    }
  }

  &__stats {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 14px;

    @media (max-width: 1100px) { grid-template-columns: repeat(3, 1fr); }
    @media (max-width: 700px)  { grid-template-columns: 1fr; }
  }

  // 예비 서버가 돌았다는 것은 장애 신호라 카드 자체를 눈에 띄게 물들입니다.
  &__card--alert :deep(.a-card) {
    border-color: $p-warn;
    background: $p-warn-soft;
  }

  &__stats-note {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: -8px;
    font-size: 11px;
    color: $p-ink-soft;

    > i { font-size: 14px; }
  }

  &__filters {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    gap: 10px;
  }

  &__f {
    flex: 0 0 auto;

    &--sm { width: 120px; }
    &--md { width: 160px; }
  }

  &__f-btn {
    display: flex;
    align-items: flex-end;
    padding-bottom: 1px;
    margin-left: auto;
  }

  &__retain {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: 12px;
    font-size: 11px;
    color: $p-ink-soft;

    > i { font-size: 14px; }
  }

  &__sec-title {
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }

  &__sec-icon {
    font-size: 18px !important;
    color: $p-point-deep;
  }

  &__mono {
    font-family: $p-font-mono;
    font-size: 12px;
    font-variant-numeric: tabular-nums;
  }

  &__sm { font-size: 11px; }

  &__dash { color: $p-ink-softer; }

  &__bad {
    color: $p-err-ink;
    font-weight: 800;
  }

  &__model {
    display: inline-block;
    max-width: 220px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    vertical-align: bottom;
  }

  &__profile {
    display: flex;
    flex-direction: column;
    gap: 2px;
    align-items: flex-start;
  }

  &__provider {
    font-size: 11px;
    color: $p-ink-soft;
  }

  &__badges,
  &__result {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
  }

  &__err {
    font-size: 11px;
    color: $p-err-ink;
    max-width: 180px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__tokens {
    display: flex;
    flex-direction: column;
    gap: 1px;
    align-items: flex-end;

    &-sub {
      font-size: 10px;
      color: $p-ink-soft;
      font-family: $p-font-mono;
    }
  }

  &__pager {
    padding: 8px 16px 14px;
  }
}
</style>
